import { CloseIcon, CheckIcon, WarningIcon, InfoIcon } from "./icons";
import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import { IconButton } from "./IconButton";

export type FeedbackTone = "neutral" | "success" | "warning" | "danger";

export interface InlineMessageProps {
  children: ReactNode;
  tone?: FeedbackTone;
  level?: "field" | "section";
  className?: string;
}

/** Field messages are unboxed; section messages use a tinted V2 block and icon tile. */
export function InlineMessage({
  children,
  tone = "neutral",
  level = "section",
  className,
}: InlineMessageProps) {
  return (
    <div
      role={tone === "danger" && level === "section" ? "alert" : undefined}
      className={cn(
        "studio-message flex gap-2.5 leading-5",
        level === "field" ? "items-center text-xs" : "items-start rounded-md px-4 py-[14px] text-[13px]",
        level === "field" ? fieldTone[tone] : sectionTone[tone],
        className,
      )}
    >
      <FeedbackMark tone={tone} compact={level === "field"} />
      <span className={cn("min-w-0 flex-1", level === "section" && "py-1")}>{children}</span>
    </div>
  );
}

export interface BannerProps extends InlineMessageProps {
  action?: ReactNode;
  onDismiss?: () => void;
  dismissLabel?: string;
  live?: boolean;
}

/** Persistent, full-width condition at the top of the region it concerns. */
export function Banner({
  children,
  tone = "neutral",
  action,
  onDismiss,
  dismissLabel = "Dismiss",
  live = false,
  className,
}: BannerProps) {
  if (live) {
    return (
      <div
        role="status"
        aria-atomic="true"
        className={cn(
          "studio-banner flex min-w-0 flex-wrap items-center gap-3 rounded-lg bg-accent-subtle px-5 py-4 text-[13px] text-accent-text",
          className,
        )}
      >
        <span
          aria-hidden="true"
          className="grid size-7 shrink-0 place-items-center rounded-sm bg-accent text-xs font-bold text-white"
        >
          <InfoIcon className="size-4 shrink-0" />
        </span>
        <span className="studio-banner-copy min-w-0 flex-1 leading-5">{children}</span>
        {action ? <span className="studio-banner-action inline-flex items-center">{action}</span> : null}
      </div>
    );
  }
  return (
    <div
      className={cn(
        "studio-banner flex min-w-0 flex-wrap items-center gap-3 rounded-md px-4 py-[14px] text-[13px]",
        sectionTone[tone],
        className,
      )}
    >
      <FeedbackMark tone={tone} />
      <span className="studio-banner-copy min-w-0 flex-1 leading-5">{children}</span>
      {action ? <span className="studio-banner-action inline-flex items-center">{action}</span> : null}
      {onDismiss ? (
        <IconButton
          size="sm"
          aria-label={dismissLabel}
          onClick={onDismiss}
          className="studio-banner-dismiss text-current"
        >
          <CloseIcon className="size-4" />
        </IconButton>
      ) : null}
    </div>
  );
}

const fieldTone: Record<FeedbackTone, string> = {
  neutral: "text-ink-soft",
  success: "text-success-text",
  warning: "text-warning-text",
  danger: "text-danger-text",
};

const sectionTone: Record<FeedbackTone, string> = {
  neutral: "bg-canvas-soft text-ink-soft",
  success: "bg-success-subtle text-success-text",
  warning: "bg-warning-subtle text-warning-text",
  danger: "bg-danger-subtle text-danger-text",
};

const feedbackTile: Record<FeedbackTone, string> = {
  neutral: "bg-action-bg text-white",
  success: "bg-success text-white",
  warning: "bg-warning text-[var(--warning-on)]",
  danger: "bg-danger text-white",
};

function FeedbackMark({
  tone,
  compact = false,
}: {
  tone: FeedbackTone;
  compact?: boolean;
}) {
  const Mark = tone === "success" ? CheckIcon : tone === "warning" ? WarningIcon : tone === "danger" ? CloseIcon : InfoIcon;
  return (
    <span
      aria-hidden="true"
      className={cn(
        "grid shrink-0 place-items-center font-semibold",
        compact ? "size-[15px]" : "size-7 rounded-sm text-[13px]",
        !compact && feedbackTile[tone],
      )}
    >
      <Mark className={compact ? "size-3.5 shrink-0" : "size-4 shrink-0"} />
    </span>
  );
}
